/**
 * The block half of the Markdown converter: the CommonMark parsing strategy, one line
 * at a time. Each line first walks down the open containers (block quotes, list items)
 * to see how far it continues them, then may open new blocks, and whatever is left is
 * text for the innermost one — including "lazy" paragraph continuation lines.
 */

import {
  extractReferences,
  type ReferenceMap,
} from "@/lib/tool-kit/developer/markdown/inlines";
import { unescapeString } from "@/lib/tool-kit/developer/markdown/text";

export type BlockType =
  | "document"
  | "blockquote"
  | "list"
  | "item"
  | "paragraph"
  | "heading"
  | "thematic"
  | "code"
  | "html"
  | "table";

export type TableAlign = "" | "left" | "center" | "right";

export interface ListData {
  ordered: boolean;
  bulletChar: string;
  start: number;
  delimiter: string;
  markerOffset: number;
  padding: number;
}

export interface BlockNode {
  type: BlockType;
  parent: BlockNode | null;
  children: BlockNode[];
  open: boolean;
  startLine: number;
  endLine: number;
  /** Raw text: paragraph and heading inline source, code and HTML literal. */
  content: string;
  /** Link reference definitions were taken out of this paragraph. */
  hadReferences: boolean;
  level: number;
  fenced: boolean;
  fenceChar: string;
  fenceLength: number;
  fenceOffset: number;
  info: string;
  list: ListData | null;
  tight: boolean;
  htmlType: number;
  align: TableAlign[];
  /** Raw cell text; the first row is the header. */
  rows: string[][];
}

export interface BlockOptions {
  gfm: boolean;
  html: boolean;
}

const CODE_INDENT = 4;
const TAB_STOP = 4;
/** Deeper block quotes and lists than this are read as text — no real document nests 64 deep. */
const MAX_NESTING = 64;

const RE_ATX = /^#{1,6}(?:[ \t]+|$)/;
const RE_ATX_CLOSE_ONLY = /^[ \t]*#+[ \t]*$/;
const RE_ATX_CLOSE = /[ \t]+#+[ \t]*$/;
const RE_FENCE_OPEN = /^`{3,}(?!.*`)|^~{3,}/;
const RE_FENCE_CLOSE = /^(?:`{3,}|~{3,})(?=[ \t]*$)/;
const RE_SETEXT = /^(?:=+|-+)[ \t]*$/;
const RE_THEMATIC = /^(?:\*[ \t]*){3,}$|^(?:_[ \t]*){3,}$|^(?:-[ \t]*){3,}$/;
const RE_BULLET = /^[*+-]/;
const RE_ORDERED = /^(\d{1,9})([.)])/;
const RE_MAYBE_SPECIAL = /^[#`~*+_=<>0-9|:-]/;
const RE_NON_SPACE = /[^ \t\f\v\r\n]/;
const RE_BLANK_LINE = /^[ \t]*$/;
const RE_TRAILING_BLANK_LINES = /(?:\n[ \t]*)+$/;
const RE_DELIMITER_CELL = /^:?-+:?$/;
const RE_LINE_ENDING = /\r\n?/g;

/** The seven ways an HTML block can start (index 0 is unused). */
const RE_HTML_OPEN: readonly RegExp[] = [
  /^$/,
  /^<(?:script|pre|textarea|style)(?:\s|>|$)/i,
  /^<!--/,
  /^<[?]/,
  /^<![A-Za-z]/,
  /^<!\[CDATA\[/,
  /^<[/]?(?:address|article|aside|base|basefont|blockquote|body|caption|center|col|colgroup|dd|details|dialog|dir|div|dl|dt|fieldset|figcaption|figure|footer|form|frame|frameset|h[1-6]|head|header|hr|html|iframe|legend|li|link|main|menu|menuitem|nav|noframes|ol|optgroup|option|p|param|search|section|summary|table|tbody|td|tfoot|th|thead|title|tr|track|ul)(?:\s|[/]?[>]|$)/i,
  /^(?:<[A-Za-z][A-Za-z0-9-]*(?:\s+[a-zA-Z_:][a-zA-Z0-9_.:-]*(?:\s*=\s*(?:[^"'=<>`\s]+|'[^']*'|"[^"]*"))?)*\s*\/?>|<\/[A-Za-z][A-Za-z0-9-]*\s*>)\s*$/,
];

/** How HTML blocks of types 1–5 end; 6 and 7 end at a blank line. */
const RE_HTML_CLOSE: readonly RegExp[] = [
  /^$/,
  /<\/(?:script|pre|textarea|style)>/i,
  /-->/,
  /\?>/,
  />/,
  /\]\]>/,
];

function makeBlock(type: BlockType, line: number): BlockNode {
  return {
    type,
    parent: null,
    children: [],
    open: true,
    startLine: line,
    endLine: line,
    content: "",
    hadReferences: false,
    level: 0,
    fenced: false,
    fenceChar: "",
    fenceLength: 0,
    fenceOffset: 0,
    info: "",
    list: null,
    tight: true,
    htmlType: 0,
    align: [],
    rows: [],
  };
}

function isSpaceOrTab(c: string | undefined): boolean {
  return c === " " || c === "\t";
}

function acceptsLines(type: BlockType): boolean {
  return type === "paragraph" || type === "code" || type === "html";
}

function canContain(parent: BlockType, child: BlockType): boolean {
  if (parent === "list") {
    return child === "item";
  }
  if (parent === "document" || parent === "blockquote" || parent === "item") {
    return child !== "item";
  }
  return false;
}

function depthOf(block: BlockNode): number {
  let depth = 0;
  let node: BlockNode | null = block;
  while (node) {
    depth += 1;
    node = node.parent;
  }
  return depth;
}

function listsMatch(a: ListData | null, b: ListData): boolean {
  return (
    a !== null &&
    a.ordered === b.ordered &&
    a.delimiter === b.delimiter &&
    a.bulletChar === b.bulletChar
  );
}

function removeBlock(block: BlockNode): void {
  const siblings = block.parent?.children;
  if (siblings) {
    siblings.splice(siblings.indexOf(block), 1);
  }
}

function replaceBlock(old: BlockNode, replacement: BlockNode): void {
  const siblings = old.parent?.children;
  replacement.parent = old.parent;
  if (siblings) {
    siblings[siblings.indexOf(old)] = replacement;
  }
}

/** Split a GFM table row into raw cells; `\|` is a literal pipe. */
export function splitTableRow(line: string): string[] {
  let text = line.trim();
  if (text.startsWith("|")) {
    text = text.slice(1);
  }
  if (text.endsWith("|") && !text.endsWith("\\|")) {
    text = text.slice(0, -1);
  }
  const cells: string[] = [];
  let current = "";
  let index = 0;
  while (index < text.length) {
    const ch = text[index];
    if (ch === "\\" && index + 1 < text.length) {
      const next = text[index + 1];
      current += next === "|" ? "|" : `${ch}${next}`;
      index += 2;
      continue;
    }
    if (ch === "|") {
      cells.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
    index += 1;
  }
  cells.push(current.trim());
  return cells;
}

function delimiterRowAlignment(line: string): TableAlign[] | null {
  if (!line.includes("-")) {
    return null;
  }
  const align: TableAlign[] = [];
  for (const cell of splitTableRow(line)) {
    if (!RE_DELIMITER_CELL.test(cell)) {
      return null;
    }
    const left = cell.startsWith(":");
    const right = cell.endsWith(":");
    if (left && right) {
      align.push("center");
    } else if (left) {
      align.push("left");
    } else if (right) {
      align.push("right");
    } else {
      align.push("");
    }
  }
  return align;
}

interface ListMarker {
  length: number;
  ordered: boolean;
  bullet: string;
  start: number;
  delimiter: string;
}

function matchListMarker(
  rest: string,
  container: BlockNode
): ListMarker | null {
  const bullet = RE_BULLET.exec(rest);
  if (bullet) {
    return {
      length: 1,
      ordered: false,
      bullet: bullet[0],
      start: 1,
      delimiter: "",
    };
  }
  const ordered = RE_ORDERED.exec(rest);
  // Only a list starting at 1 may interrupt a paragraph.
  if (ordered && (container.type !== "paragraph" || ordered[1] === "1")) {
    return {
      length: ordered[0].length,
      ordered: true,
      bullet: "",
      start: Number.parseInt(ordered[1], 10),
      delimiter: ordered[2],
    };
  }
  return null;
}

class BlockParser {
  readonly doc: BlockNode = makeBlock("document", 1);
  tip: BlockNode;
  oldtip: BlockNode;
  lastMatchedContainer: BlockNode;
  readonly refs: ReferenceMap = new Map();
  readonly options: BlockOptions;
  line = "";
  lineNumber = 0;
  offset = 0;
  column = 0;
  nextNonspace = 0;
  nextNonspaceColumn = 0;
  indent = 0;
  indented = false;
  blank = false;
  partiallyConsumedTab = false;
  allClosed = true;

  constructor(options: BlockOptions) {
    this.options = options;
    this.tip = this.doc;
    this.oldtip = this.doc;
    this.lastMatchedContainer = this.doc;
  }

  parse(source: string): BlockNode {
    const normalized = source.replace(RE_LINE_ENDING, "\n");
    const lines = normalized.split("\n");
    const count = normalized.endsWith("\n") ? lines.length - 1 : lines.length;
    for (let index = 0; index < count; index += 1) {
      this.incorporateLine(lines[index]);
    }
    let block: BlockNode | null = this.tip;
    while (block) {
      const parent: BlockNode | null = block.parent;
      this.finalize(block, count);
      block = parent;
    }
    return this.doc;
  }

  findNextNonspace(): void {
    let index = this.offset;
    let columns = this.column;
    let c = this.line[index];
    while (c === " " || c === "\t") {
      columns += c === " " ? 1 : TAB_STOP - (columns % TAB_STOP);
      index += 1;
      c = this.line[index];
    }
    this.blank = c === undefined;
    this.nextNonspace = index;
    this.nextNonspaceColumn = columns;
    this.indent = columns - this.column;
    this.indented = this.indent >= CODE_INDENT;
  }

  advanceNextNonspace(): void {
    this.offset = this.nextNonspace;
    this.column = this.nextNonspaceColumn;
    this.partiallyConsumedTab = false;
  }

  /** Move forward `count` characters, or `count` columns when tabs should be split. */
  advanceOffset(count: number, columns: boolean): void {
    let remaining = count;
    while (remaining > 0 && this.offset < this.line.length) {
      if (this.line[this.offset] !== "\t") {
        this.partiallyConsumedTab = false;
        this.offset += 1;
        this.column += 1;
        remaining -= 1;
        continue;
      }
      const toTab = TAB_STOP - (this.column % TAB_STOP);
      if (columns) {
        this.partiallyConsumedTab = toTab > remaining;
        const step = Math.min(toTab, remaining);
        this.column += step;
        this.offset += this.partiallyConsumedTab ? 0 : 1;
        remaining -= step;
      } else {
        this.partiallyConsumedTab = false;
        this.column += toTab;
        this.offset += 1;
        remaining -= 1;
      }
    }
  }

  addLine(): void {
    if (this.partiallyConsumedTab) {
      this.offset += 1;
      this.tip.content += " ".repeat(TAB_STOP - (this.column % TAB_STOP));
    }
    this.tip.content += `${this.line.slice(this.offset)}\n`;
  }

  addChild(type: BlockType): BlockNode {
    while (!canContain(this.tip.type, type) && this.tip.parent) {
      this.finalize(this.tip, this.lineNumber - 1);
    }
    const block = makeBlock(type, this.lineNumber);
    block.parent = this.tip;
    this.tip.children.push(block);
    this.tip = block;
    return block;
  }

  closeUnmatchedBlocks(): void {
    if (this.allClosed) {
      return;
    }
    while (this.oldtip !== this.lastMatchedContainer && this.oldtip.parent) {
      const parent = this.oldtip.parent;
      this.finalize(this.oldtip, this.lineNumber - 1);
      this.oldtip = parent;
    }
    this.allClosed = true;
  }

  finalize(block: BlockNode, lineNumber: number): void {
    block.open = false;
    block.endLine = lineNumber;
    if (block.type === "paragraph") {
      this.finalizeParagraph(block);
    } else if (block.type === "code") {
      finalizeCode(block);
    } else if (block.type === "html") {
      block.content = block.content.replace(RE_TRAILING_BLANK_LINES, "");
    } else if (block.type === "item") {
      block.endLine = block.children.at(-1)?.endLine ?? block.startLine;
    } else if (block.type === "list") {
      finalizeList(block);
    }
    if (block.parent) {
      this.tip = block.parent;
    }
  }

  finalizeParagraph(block: BlockNode): void {
    const rest = extractReferences(block.content, this.refs);
    if (rest !== block.content) {
      block.hadReferences = true;
      block.content = rest;
    }
    if (block.hadReferences && !RE_NON_SPACE.test(block.content)) {
      removeBlock(block);
    }
  }

  incorporateLine(line: string): void {
    this.line = line;
    this.lineNumber += 1;
    this.offset = 0;
    this.column = 0;
    this.blank = false;
    this.partiallyConsumedTab = false;
    this.oldtip = this.tip;

    const matched = this.matchOpenBlocks();
    if (matched === null) {
      return;
    }
    let container = matched;
    this.allClosed = container === this.oldtip;
    this.lastMatchedContainer = container;

    let matchedLeaf =
      container.type !== "paragraph" && acceptsLines(container.type);
    while (!matchedLeaf) {
      this.findNextNonspace();
      if (
        !(
          this.indented ||
          RE_MAYBE_SPECIAL.test(this.line[this.nextNonspace] ?? "")
        )
      ) {
        this.advanceNextNonspace();
        break;
      }
      const result = this.tryBlockStarts(container);
      if (result === 0) {
        this.advanceNextNonspace();
        break;
      }
      container = this.tip;
      matchedLeaf = result === 2;
    }
    this.addText(container);
  }

  /** Step 1: how far down the open blocks this line continues. Null: line consumed. */
  matchOpenBlocks(): BlockNode | null {
    let container = this.doc;
    let child = container.children.at(-1);
    while (child?.open) {
      container = child;
      this.findNextNonspace();
      const result = this.continueBlock(container);
      if (result === 2) {
        return null;
      }
      if (result === 1) {
        return container.parent ?? this.doc;
      }
      child = container.children.at(-1);
    }
    return container;
  }

  /** Step 3: whatever is left of the line is text for the innermost block. */
  addText(container: BlockNode): void {
    if (!(this.allClosed || this.blank) && this.tip.type === "paragraph") {
      this.addLine();
      return;
    }
    this.closeUnmatchedBlocks();
    if (acceptsLines(container.type)) {
      this.addLine();
      const type = container.htmlType;
      if (
        container.type === "html" &&
        type >= 1 &&
        type <= 5 &&
        RE_HTML_CLOSE[type].test(this.line.slice(this.offset))
      ) {
        this.finalize(container, this.lineNumber);
      }
    } else if (container.type === "table") {
      if (!this.blank && this.offset < this.line.length) {
        container.rows.push(splitTableRow(this.line.slice(this.offset)));
      }
    } else if (this.offset < this.line.length && !this.blank) {
      this.addChild("paragraph");
      this.advanceNextNonspace();
      this.addLine();
    }
  }

  /** 0: continues; 1: does not; 2: the line was a closing code fence. */
  continueBlock(block: BlockNode): number {
    switch (block.type) {
      case "document":
      case "list":
        return 0;
      case "blockquote":
        return this.continueBlockquote();
      case "item":
        return this.continueItem(block);
      case "code":
        return block.fenced
          ? this.continueFence(block)
          : this.continueIndented();
      case "html":
        return this.blank && block.htmlType >= 6 ? 1 : 0;
      case "paragraph":
      case "table":
        return this.blank ? 1 : 0;
      default:
        return 1;
    }
  }

  continueBlockquote(): number {
    if (this.indented || this.line[this.nextNonspace] !== ">") {
      return 1;
    }
    this.advanceNextNonspace();
    this.advanceOffset(1, false);
    if (isSpaceOrTab(this.line[this.offset])) {
      this.advanceOffset(1, true);
    }
    return 0;
  }

  continueItem(item: BlockNode): number {
    const data = item.list as ListData;
    if (this.blank) {
      if (item.children.length === 0) {
        return 1;
      }
      this.advanceNextNonspace();
      return 0;
    }
    if (this.indent >= data.markerOffset + data.padding) {
      this.advanceOffset(data.markerOffset + data.padding, true);
      return 0;
    }
    return 1;
  }

  continueFence(block: BlockNode): number {
    const rest = this.line.slice(this.nextNonspace);
    const close =
      this.indent <= 3 && rest[0] === block.fenceChar
        ? RE_FENCE_CLOSE.exec(rest)
        : null;
    if (close && close[0].length >= block.fenceLength) {
      this.finalize(block, this.lineNumber);
      return 2;
    }
    let strip = block.fenceOffset;
    while (strip > 0 && isSpaceOrTab(this.line[this.offset])) {
      this.advanceOffset(1, true);
      strip -= 1;
    }
    return 0;
  }

  continueIndented(): number {
    if (this.indent >= CODE_INDENT) {
      this.advanceOffset(CODE_INDENT, true);
      return 0;
    }
    if (this.blank) {
      this.advanceNextNonspace();
      return 0;
    }
    return 1;
  }

  /** Step 2: 0 when nothing starts; 1 for a new container; 2 for a new leaf. */
  tryBlockStarts(container: BlockNode): number {
    return (
      this.startBlockquote(container) ||
      this.startAtxHeading() ||
      this.startFence() ||
      this.startHtmlBlock(container) ||
      this.startSetextHeading(container) ||
      this.startThematicBreak() ||
      this.startListItem(container) ||
      this.startIndentedCode() ||
      this.startTable(container)
    );
  }

  startBlockquote(container: BlockNode): number {
    if (
      this.indented ||
      this.line[this.nextNonspace] !== ">" ||
      depthOf(container) >= MAX_NESTING
    ) {
      return 0;
    }
    this.advanceNextNonspace();
    this.advanceOffset(1, false);
    if (isSpaceOrTab(this.line[this.offset])) {
      this.advanceOffset(1, true);
    }
    this.closeUnmatchedBlocks();
    this.addChild("blockquote");
    return 1;
  }

  startAtxHeading(): number {
    if (this.indented) {
      return 0;
    }
    const match = RE_ATX.exec(this.line.slice(this.nextNonspace));
    if (!match) {
      return 0;
    }
    this.advanceNextNonspace();
    this.advanceOffset(match[0].length, false);
    this.closeUnmatchedBlocks();
    const heading = this.addChild("heading");
    heading.level = match[0].trim().length;
    heading.content = this.line
      .slice(this.offset)
      .replace(RE_ATX_CLOSE_ONLY, "")
      .replace(RE_ATX_CLOSE, "");
    this.advanceOffset(this.line.length - this.offset, false);
    return 2;
  }

  startFence(): number {
    if (this.indented) {
      return 0;
    }
    const match = RE_FENCE_OPEN.exec(this.line.slice(this.nextNonspace));
    if (!match) {
      return 0;
    }
    this.closeUnmatchedBlocks();
    const block = this.addChild("code");
    block.fenced = true;
    block.fenceLength = match[0].length;
    block.fenceChar = match[0][0];
    block.fenceOffset = this.indent;
    this.advanceNextNonspace();
    this.advanceOffset(match[0].length, false);
    return 2;
  }

  startHtmlBlock(container: BlockNode): number {
    if (
      !this.options.html ||
      this.indented ||
      this.line[this.nextNonspace] !== "<"
    ) {
      return 0;
    }
    const rest = this.line.slice(this.nextNonspace);
    const lazy =
      !(this.allClosed || this.blank) && this.tip.type === "paragraph";
    for (let type = 1; type <= 7; type += 1) {
      const interrupts = type < 7 || !(container.type === "paragraph" || lazy);
      if (RE_HTML_OPEN[type].test(rest) && interrupts) {
        this.closeUnmatchedBlocks();
        this.addChild("html").htmlType = type;
        return 2;
      }
    }
    return 0;
  }

  startSetextHeading(container: BlockNode): number {
    if (this.indented || container.type !== "paragraph") {
      return 0;
    }
    const match = RE_SETEXT.exec(this.line.slice(this.nextNonspace));
    if (!match) {
      return 0;
    }
    this.closeUnmatchedBlocks();
    const rest = extractReferences(container.content, this.refs);
    if (rest !== container.content) {
      container.hadReferences = true;
      container.content = rest;
    }
    if (rest.length === 0) {
      return 0;
    }
    const heading = makeBlock("heading", container.startLine);
    heading.level = match[0][0] === "=" ? 1 : 2;
    heading.content = rest;
    replaceBlock(container, heading);
    this.tip = heading;
    this.advanceOffset(this.line.length - this.offset, false);
    return 2;
  }

  startThematicBreak(): number {
    if (
      this.indented ||
      !RE_THEMATIC.test(this.line.slice(this.nextNonspace))
    ) {
      return 0;
    }
    this.closeUnmatchedBlocks();
    this.addChild("thematic");
    this.advanceOffset(this.line.length - this.offset, false);
    return 2;
  }

  startListItem(container: BlockNode): number {
    if (
      (this.indented && container.type !== "list") ||
      depthOf(container) >= MAX_NESTING
    ) {
      return 0;
    }
    const data = this.parseListMarker(container);
    if (!data) {
      return 0;
    }
    this.closeUnmatchedBlocks();
    if (this.tip.type !== "list" || !listsMatch(container.list, data)) {
      this.addChild("list").list = data;
    }
    this.addChild("item").list = data;
    return 1;
  }

  parseListMarker(container: BlockNode): ListData | null {
    if (this.indent >= CODE_INDENT) {
      return null;
    }
    const marker = matchListMarker(
      this.line.slice(this.nextNonspace),
      container
    );
    if (!marker) {
      return null;
    }
    const after = this.line[this.nextNonspace + marker.length];
    if (!(after === undefined || isSpaceOrTab(after))) {
      return null;
    }
    const itemText = this.line.slice(this.nextNonspace + marker.length);
    // An empty list item cannot interrupt a paragraph.
    if (container.type === "paragraph" && !RE_NON_SPACE.test(itemText)) {
      return null;
    }
    const data: ListData = {
      ordered: marker.ordered,
      bulletChar: marker.bullet,
      start: marker.start,
      delimiter: marker.delimiter,
      markerOffset: this.indent,
      padding: 0,
    };
    this.advanceNextNonspace();
    this.advanceOffset(marker.length, true);
    data.padding = marker.length + this.measureItemSpacing();
    return data;
  }

  /** Spaces between a list marker and its content: 1–4, or 1 when there are more. */
  measureItemSpacing(): number {
    const startColumn = this.column;
    const startOffset = this.offset;
    do {
      this.advanceOffset(1, true);
    } while (
      this.column - startColumn < 5 &&
      isSpaceOrTab(this.line[this.offset])
    );
    const blankItem = this.line[this.offset] === undefined;
    const spaces = this.column - startColumn;
    if (spaces >= 5 || spaces < 1 || blankItem) {
      this.column = startColumn;
      this.offset = startOffset;
      if (isSpaceOrTab(this.line[this.offset])) {
        this.advanceOffset(1, true);
      }
      return 1;
    }
    return spaces;
  }

  startIndentedCode(): number {
    if (!this.indented || this.tip.type === "paragraph" || this.blank) {
      return 0;
    }
    this.advanceOffset(CODE_INDENT, true);
    this.closeUnmatchedBlocks();
    this.addChild("code");
    return 2;
  }

  /** A GFM delimiter row under a paragraph turns its last line into a table header. */
  startTable(container: BlockNode): number {
    if (!this.options.gfm || this.indented || container.type !== "paragraph") {
      return 0;
    }
    const align = delimiterRowAlignment(this.line.slice(this.nextNonspace));
    if (!align) {
      return 0;
    }
    const content = container.content.replace(RE_TRAILING_BLANK_LINES, "");
    const lastBreak = content.lastIndexOf("\n");
    const header = splitTableRow(content.slice(lastBreak + 1));
    if (header.length !== align.length) {
      return 0;
    }
    this.closeUnmatchedBlocks();
    const before = lastBreak === -1 ? "" : content.slice(0, lastBreak + 1);
    if (RE_NON_SPACE.test(before)) {
      container.content = before;
      this.finalize(container, this.lineNumber - 2);
    } else {
      removeBlock(container);
      this.tip = container.parent ?? this.doc;
    }
    const table = this.addChild("table");
    table.startLine = this.lineNumber - 1;
    table.align = align;
    table.rows = [header];
    this.advanceOffset(this.line.length - this.offset, false);
    return 2;
  }
}

function finalizeCode(block: BlockNode): void {
  if (block.fenced) {
    const newline = block.content.indexOf("\n");
    block.info = unescapeString(block.content.slice(0, newline).trim());
    block.content = block.content.slice(newline + 1);
    return;
  }
  const lines = block.content.split("\n");
  while (lines.length > 0 && RE_BLANK_LINE.test(lines.at(-1) ?? "")) {
    lines.pop();
  }
  block.content = `${lines.join("\n")}\n`;
}

/** A gap of a line or more between two blocks makes the list they are in loose. */
function gapBetween(block: BlockNode, next: BlockNode | undefined): boolean {
  return next !== undefined && block.endLine !== next.startLine - 1;
}

function finalizeList(list: BlockNode): void {
  list.tight = !list.children.some(
    (item, index) =>
      gapBetween(item, list.children[index + 1]) ||
      item.children.some((child, childIndex) =>
        gapBetween(child, item.children[childIndex + 1])
      )
  );
  list.endLine = list.children.at(-1)?.endLine ?? list.startLine;
}

/** Parse Markdown into a block tree plus the link reference definitions it declares. */
export function parseBlocks(
  source: string,
  options: BlockOptions
): { doc: BlockNode; refs: ReferenceMap } {
  const parser = new BlockParser(options);
  const doc = parser.parse(source);
  return { doc, refs: parser.refs };
}
